import { messagesService } from "@shared/services/messages.ts";
import { permissions } from "@shared/services/permissions.ts";
import { usersService } from "@shared/services/user.ts";
import { useSubscriptionsStore } from "@shared/stores/subscriptions.ts";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import dateUtils from "@shared/utils/date-utils.ts";
import logger from "@shared/utils/logger.ts";
import { DateTime } from "luxon";
import { computed, ref } from "vue";
import { useTokenGenerationStore } from "@shared/stores/tokenGeneration.ts";
import { SubscriptionDTO } from "@shared/types/common";

export function useTokenGeneration() {
  const subscriptionsStore = useSubscriptionsStore();
  const confirmRegenerateVisible = ref(false);
  const loading = ref(false);
  const tokenGenerationStore = useTokenGenerationStore();

  const currentSubscription = computed(() => {
    return subscriptionsStore.currentSubscription;
  });

  const onClickGenerateToken = () => {
    if (tokenGenerationStore.apiKey.length && !isTokenExpired.value) {
      confirmRegenerateVisible.value = true;
      return;
    }

    generateToken();
  };

  const generateToken = async () => {
    if (!currentSubscription.value) {
      return;
    }

    confirmRegenerateVisible.value = false;
    loading.value = true;
    try {
      const response = await usersService.generateAdminApiToken({
        accountNumber: currentSubscription.value.accountNumber,
      });

      if (!response) {
        throw new Error("Invalid token");
      }

      tokenGenerationStore.setApiKey(response.token);
      tokenGenerationStore.setApiKeyExpiry(response.tokenExpiry);

      useNotifications().success("API Token generated successfully.");
    } catch (e) {
      useNotifications().error(messagesService.token_generation_error, {
        duration: 4500,
      });
      logger.error(e);
    }
    loading.value = false;
  };

  const getApiToken = async (currentSubscription: SubscriptionDTO) => {
    tokenGenerationStore.setSsTokenLoading(true);
    const hasGetApiTokenPermissions = permissions.has("apiToken:view");
    if (!hasGetApiTokenPermissions) {
      return;
    }
    try {
      const response = await usersService.getAdminApiToken({
        accountNumber: currentSubscription.accountNumber,
      });

      if (!response) {
        throw new Error("Invalid token");
      }

      tokenGenerationStore.setApiKey(response.token);
      tokenGenerationStore.setApiKeyExpiry(response.tokenExpiry);
    } catch (e) {
      useNotifications().error(messagesService.token_generation_error, {
        duration: 4500,
      });
      logger.error(e);
    }
    tokenGenerationStore.setSsTokenLoading(false);
  };

  const generateTokenBtnText = computed(() => {
    return !tokenGenerationStore.apiKey.length
      ? "Generate Token"
      : "Regenerate Token";
  });

  const hasGenerateTokenPermission = computed(() =>
    permissions.has("apiToken:write"),
  );

  const canCopyToken = computed(() => {
    return permissions.has("apiToken:view");
  });

  const isTokenExpired = computed<boolean>(() => {
    if (!tokenGenerationStore.apiKeyExpiry) {
      return true;
    }

    const expiryDate = DateTime.fromMillis(
      tokenGenerationStore.apiKeyExpiry,
    ).toUTC();

    const currentDate = DateTime.now().toUTC();

    return currentDate > expiryDate;
  });

  const tokenExpiryFormatted = computed<string[]>(() => {
    if (!tokenGenerationStore.apiKeyExpiry) {
      return [];
    }

    return [
      dateUtils.formatUTC(
        new Date(tokenGenerationStore.apiKeyExpiry),
        "MM/dd/yyyy",
      ),
      dateUtils.formatUTC(
        new Date(tokenGenerationStore.apiKeyExpiry),
        "HH:mm a",
      ),
    ];
  });

  const tokenAlertText = computed(() => {
    const text =
      "This API token gives you powerful capabilities to enhance your JFrog experience.<br/>It is crucial that you keep your API token secure.";
    const [dateFormatted, hourFormatted] = tokenExpiryFormatted.value;
    const expiredText = !isTokenExpired.value
      ? `The token will expire on: ${dateFormatted} at ${hourFormatted} (UTC).`
      : `The token has expired at: ${dateFormatted} at ${hourFormatted} (UTC).`;

    return `${text}<br/><strong>${expiredText}</strong>`;
  });

  return {
    getApiToken,
    onClickGenerateToken,
    generateToken,
    generateTokenBtnText,
    isTokenExpired,
    tokenAlertText,
    confirmRegenerateVisible,
    loading,
    hasGenerateTokenPermission,
    canCopyToken,
  };
}
